import { auditStamp, auditDate } from "../../utils/auditStamp";
import PageHeading from "../../components/PageHeading";
import { createListSearch } from "../../utils/listSearch";
import { useEffect, useState } from "react";
import { Modal, Select, message } from "antd";
import { Plus, Paperclip } from "lucide-react";
import axios from "../../axios/AxiosInstance";
import { absenceStore } from "../../services/absenceStore";

const types = [
  "Arrêt maladie",
  "Accident du travail",
  "Congé maternité / paternité",
  "Congé",
  "Absence autorisée",
  "Autre absence",
];
const empty = {
  candidate: "",
  type: "Arrêt maladie",
  start: "",
  end: "",
  comment: "",
};
const input =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm";
export default function AbsencesPage({ candidate, embedded = false }) {
  const scope = `${localStorage.getItem("userId") || "unknown"}:${localStorage.getItem("agencyName") || "unknown"}`;
  const [rows, setRows] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [open, setOpen] = useState(false);
  const [formSession, setFormSession] = useState(0);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const closeModal = () => {
    if (busy) return;
    setOpen(false);
    setForm({ ...empty, candidate: candidate?._id || "" });
    setFile(null);
  };
  useEffect(() => {
    let active = true;
    absenceStore("list", scope)
      .then((data) => {
        if (active) setRows(data);
      })
      .catch(() => {
        if (active)
          setError("Impossible de charger le suivi local des absences.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    if (candidate?._id) setCandidates([candidate]);
    else
      axios
        .get("tempworker-candidate/freelance")
        .then(({ data }) => {
          if (active) setCandidates(Array.isArray(data) ? data : []);
        })
        .catch(() => {
          if (active)
            setError(
              "Impossible de charger les candidats. Actualisez la page.",
            );
        });
    return () => {
      active = false;
    };
  }, [scope, candidate]);
  const save = async (event) => {
    event.preventDefault();
    if (busy) return;
    const candidate = candidates.find((c) => c._id === form.candidate);
    if (!candidate) {
      message.error("Sélectionnez un candidat.");
      return;
    }
    if (!form.start || !form.end || form.end < form.start) {
      message.error("Vérifiez la période de l’absence.");
      return;
    }
    if (
      !file ||
      !["application/pdf", "image/jpeg", "image/png"].includes(file.type) ||
      file.size > 10 * 1024 * 1024
    ) {
      message.error(
        "Ajoutez un justificatif PDF, JPG ou PNG de 10 Mo maximum.",
      );
      return;
    }
    setBusy(true);
    try {
      const row = {
        ...form,
        id: crypto.randomUUID(),
        scope,
        name: [candidate.firstname, candidate.lastname]
          .filter(Boolean)
          .join(" "),
        file,
        status: "À traiter",
        ...auditStamp(),
      };
      await absenceStore("save", row);
      setRows((current) => [row, ...current]);
      setForm(empty);
      setFile(null);
      setOpen(false);
      message.success("Absence enregistrée.");
    } catch {
      message.error(
        "Enregistrement impossible. Le stockage local peut être indisponible ou plein.",
      );
    } finally {
      setBusy(false);
    }
  };
  const download = (row) => {
    const url = URL.createObjectURL(row.file);
    const link = document.createElement("a");
    link.href = url;
    link.download = row.file.name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const matchesAbsence = createListSearch(search, [
    "name",
    "type",
    "comment",
    "status",
    "startDate",
    "endDate",
    "author",
  ]);
  const filtered = rows
    .filter(
      (row) =>
        (!candidate?._id ||
          String(
            row.candidate?._id || row.candidate || row.candidateId || "",
          ) === String(candidate._id)) &&
        (status === "all" || row.status === status) &&
        matchesAbsence(row),
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return (
    <div className={embedded ? "text-sm" : ""}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          {embedded ? (
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Absences</h2>
              <p className="mt-1 text-sm text-slate-500">
                Déposez le justificatif d’absence. Le traitement est assuré par
                l’agence.
              </p>
            </div>
          ) : (
            <PageHeading
              icon="absences"
              title="Absences"
              description="Déposez le justificatif d’absence. Le traitement est assuré par l’agence."
            />
          )}
        </div>
        <button
          onClick={() => {
            setForm({ ...empty, candidate: candidate?._id || "" });
            setFile(null);
            setFormSession((current) => current + 1);
            setOpen(true);
          }}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white"
        >
          <Plus size={18} />
          Nouvelle absence
        </button>
      </div>
      {/* Suivi et justificatifs stockés localement, à relier à l’API RH. */}
      <div className="mb-5 flex flex-wrap gap-3">
        <input
          aria-label="Rechercher une absence"
          placeholder="Candidat, type, commentaire…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${input} max-w-md`}
        />
        <select
          aria-label="Filtrer par état"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className={`${input} max-w-48`}
        >
          <option value="all">Tous les états</option>
          <option>À traiter</option>
          <option>Traitée</option>
        </select>
      </div>
      {error && (
        <p role="alert" className="mb-4 text-red-700">
          {error}
        </p>
      )}
      {loading ? (
        <p>Chargement…</p>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                {[
                  "Candidat",
                  "Absence",
                  "Période",
                  "Justificatif",
                  "État indicatif",
                ].map((label) => (
                  <th
                    key={label}
                    className={
                      label === "État indicatif" ? "text-center" : undefined
                    }
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.name}</strong>
                    <p className="mt-1 text-xs text-slate-500">
                      {row.author} · {auditDate(row.createdAt)}
                    </p>
                  </td>
                  <td>
                    {row.type}
                    {row.comment && (
                      <p className="mt-1 text-xs text-slate-500">
                        {row.comment}
                      </p>
                    )}
                  </td>
                  <td>
                    {new Date(`${row.start}T12:00:00`).toLocaleDateString(
                      "fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" },
                    )}{" "}
                    →{" "}
                    {new Date(`${row.end}T12:00:00`).toLocaleDateString(
                      "fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" },
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => download(row)}
                      className="inline-flex items-center gap-2 text-blue-700 hover:underline"
                    >
                      <Paperclip size={16} />
                      {row.file.name}
                    </button>
                  </td>
                  <td className="text-center align-middle">
                    <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr>
                  <td colSpan={5} className="p-10 text-center text-slate-500">
                    Aucune absence enregistrée pour ces critères.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Modal
        open={open}
        centered
        title="Nouvelle absence"
        onCancel={closeModal}
        footer={null}
        keyboard={!busy}
        closable={!busy}
        maskClosable={!busy}
      >
        <form key={formSession} onSubmit={save} className="mt-5 space-y-4">
          <p className="text-sm text-slate-500">
            L’agence traite les absences et met à jour leur état. Vous déposez
            uniquement le justificatif et les informations nécessaires.
          </p>
          <p className="text-xs text-slate-500">
            Le dépôt est actuellement enregistré sur cet appareil ; la
            transmission à l’agence reste à connecter.
          </p>
          {!candidate && (
            <div>
              <label className="mb-1 block text-sm font-medium">Candidat</label>
              <Select
                aria-label="Candidat"
                className="w-full"
                showSearch
                optionFilterProp="label"
                placeholder="Rechercher un candidat"
                value={form.candidate || undefined}
                onChange={(value) => setForm({ ...form, candidate: value })}
                options={candidates.map((c) => ({
                  value: c._id,
                  label: [c.firstname, c.lastname].filter(Boolean).join(" "),
                }))}
              />
            </div>
          )}
          <label className="block text-sm font-medium">
            Type d’absence
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className={`${input} mt-1`}
            >
              {types.map((type) => (
                <option key={type}>{type}</option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm font-medium">
              Du
              <input
                type="date"
                required
                value={form.start}
                onChange={(e) => setForm({ ...form, start: e.target.value })}
                className={`${input} mt-1`}
              />
            </label>
            <label className="text-sm font-medium">
              Au
              <input
                type="date"
                required
                min={form.start}
                value={form.end}
                onChange={(e) => setForm({ ...form, end: e.target.value })}
                className={`${input} mt-1`}
              />
            </label>
          </div>
          <label className="block text-sm font-medium">
            Justificatif
            <input
              type="file"
              required
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files[0] || null)}
              className={`${input} mt-1`}
            />
            <span className="text-xs font-normal text-slate-500">
              PDF, JPG ou PNG · 10 Mo maximum
            </span>
          </label>
          <label className="block text-sm font-medium">
            Commentaire
            <textarea
              rows={2}
              maxLength={1000}
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              className={`${input} mt-1`}
            />
          </label>
          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              disabled={busy}
              onClick={closeModal}
              className="rounded-lg border border-slate-200 px-4 py-2"
            >
              Annuler
            </button>
            <button
              disabled={busy}
              className="rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white"
            >
              {busy ? "Enregistrement…" : "Déposer le justificatif"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
